import { groupBy, upperFirst } from "lodash-es";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  ExceptionStatus,
  ListSpaceExceptionsQueryVariables,
} from "~/operations";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { add, startOfYear } from "~/lib/date";
import { usePaginate } from "~/components/pagination/usePaginate";
import { useSnackbar } from "notistack";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "~/components/Filter/QuickFilterMenu/QuickFilterMenu";

export type ExceptionsFilterVariables = Pick<
  NonNullable<ListSpaceExceptionsQueryVariables>["input"],
  "filter"
>;

export type UsePolicyExceptionsFilterContextParams = {
  scopeMrn: string;
  paginate?: ReturnType<typeof usePaginate>;
};

export type UsePolicyExceptionsFilterContextOutput = {
  filterVariables: ExceptionsFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function usePolicyExceptionsFilterContext({
  scopeMrn,
  paginate,
}: UsePolicyExceptionsFilterContextParams): UsePolicyExceptionsFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const statusFilterParams = searchParams.getAll("status");
  const expiresParam = searchParams.get("expires");
  const exceptionIdParam = searchParams.get("exceptionId");

  const statusFilters = statusFilterParams.every((statusFilterParam) =>
    isExceptionStatus(statusFilterParam),
  )
    ? statusFilterParams
    : undefined;

  const availableExpiresFilters = getAvailableExpiresFilters();
  const expiresFilter = isValidTimeFilterParam(expiresParam)
    ? availableExpiresFilters[expiresParam]
    : undefined;

  const exceptionIdFilter = exceptionIdParam ? exceptionIdParam : undefined;

  const filterVariables: ExceptionsFilterVariables = useMemo(() => {
    return {
      filter: {
        ...(exceptionIdFilter ? { id: exceptionIdFilter } : {}),
        ...(statusFilters?.length ? { statuses: statusFilters } : {}),
        ...(expiresFilter
          ? { validUntilRange: expiresFilter.filterValue }
          : {}),
      },
    };
  }, [JSON.stringify(statusFilterParams), exceptionIdParam, expiresParam]);

  const statusSuggestions = useExceptionsStatusSuggestions(
    scopeMrn,
    filterVariables,
    "",
  );
  const expiresSuggestions = useExceptionsExpiresSuggestions("");

  const resetCursorUrl = (href: string) => {
    return paginate?.resetCursorUrl(href) ?? new URL(href);
  };

  const addFilter = (param: string, value: string, multiple = false) => {
    const url = resetCursorUrl(location.href);
    if (multiple) {
      if (!url.searchParams.has(param, value)) {
        url.searchParams.append(param, value);
      }
    } else {
      url.searchParams.set(param, value);
    }
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string, value?: string) => {
    const url = resetCursorUrl(location.href);
    url.searchParams.delete(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const setFilter = (param: string, value: string) => {
    const url = resetCursorUrl(location.href);
    url.searchParams.set(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = resetCursorUrl(location.href);
    url.searchParams.delete("scopeType");
    url.searchParams.delete("status");
    url.searchParams.delete("expires");
    url.searchParams.delete("exceptionId");
    navigate(url.href.replace(url.origin, ""));
  };

  const activeFilters = [
    statusFilters
      ? statusFilters.map((statusFilter) => {
          return {
            label: "Status",
            value: formatStatusLabel(statusFilter),
            onClick: () => removeFilter("status", statusFilter),
          };
        })
      : [],
    expiresFilter
      ? {
          label: "Expires",
          value: expiresFilter.label,
          onClick: () => removeFilter("expires", expiresFilter.value),
        }
      : [],
    exceptionIdFilter
      ? {
          label: "Exception ID",
          value: exceptionIdFilter,
          onClick: () => removeFilter("exceptionId", exceptionIdFilter),
        }
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useExceptionsSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeMrn,
    filterVariables,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: () => {
      setFocusedField(undefined);
      setSearchInput("");
      enqueueSnackbar(
        "Freeform search text is not supported at this time. Please choose a provided value.",
        { variant: "error" },
      );
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value, o.isMultiple),
    }));
  };

  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Status",
      activeCount: statusFilters?.length || 0,
      menuOptions: quickMenuOptions("status", statusSuggestions.suggestions),
    },
    {
      label: "Expires",
      activeCount: expiresFilter ? 1 : 0,
      menuOptions: quickMenuOptions("expires", expiresSuggestions.suggestions),
    },
  ]);

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar: filterBar,
    quickFilterBar: quickFilterBar,
    activeFilterBar,
  });

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useExceptionsSuggestions(
  field: string,
  searchInput: string,
  scopeMrn: string,
  filterVars: ExceptionsFilterVariables,
) {
  const categorySuggestions = useExceptionsCategorySuggestions(searchInput);
  const statusSuggestions = useExceptionsStatusSuggestions(
    scopeMrn,
    filterVars,
    searchInput,
  );
  const expiresSuggestions = useExceptionsExpiresSuggestions(
    field === "expires" ? searchInput : "",
  );
  switch (field) {
    case "status":
      return statusSuggestions;
    case "expires":
      return expiresSuggestions;
    default:
      return categorySuggestions;
  }
}

function useExceptionsCategorySuggestions(searchInput: string) {
  const statusFilterGroup: FilterMenuOption = {
    field: "status",
    label: "Status",
    value: "status",
  };

  const expiresFilterGroup: FilterMenuOption = {
    field: "expires",
    label: "Expires",
    value: "expires",
  };

  const suggestions = [statusFilterGroup, expiresFilterGroup].filter((s) =>
    s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
  );
  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useExceptionsStatusSuggestions(
  resourceMrn: string,
  filterVars: ExceptionsFilterVariables,
  searchInput: string,
) {
  // const [getSuggestions, { data, loading, error, previousData }] =
  //   useGetExceptionFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return Object.values(ExceptionStatus).flatMap((status) => {
      return searchText
        ? status.toLowerCase().includes(searchText)
          ? status
          : []
        : status;
    });
  };

  const param = "status";

  const suggestionsData =
    fetchSuggestions(searchInput).flatMap((suggestion, index) => {
      const value = suggestion;
      return {
        param,
        field: `status.${index}`,
        value: value,
        label: formatStatusLabel(value),
        isMultiple: true,
      };
    }) || [];

  const loading = false;
  const error = undefined;
  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useExceptionsExpiresSuggestions(searchInput: string) {
  const availableTimeFilters = getAvailableExpiresFilters();
  const suggestions = Object.entries(availableTimeFilters)
    .map(([value, { label }]) => {
      return { label, value };
    })
    .map((s, index) => ({ ...s, field: `expires.${index}`, param: "expires" }))
    .filter((s) =>
      s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    );

  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function isExceptionStatus(value: any): value is ExceptionStatus {
  return Object.values(ExceptionStatus).includes(value);
}

function getAvailableExpiresFilters() {
  const now = new Date();
  return {
    NEXT_WEEK: {
      label: "Expiring Within Next Week",
      value: "NEXT_WEEK",
      filterValue: {
        from: now.toISOString(),
        to: add(now, { weeks: 1 }).toISOString(),
      },
    },
    NEXT_MONTH: {
      label: "Expiring Within Next Month",
      value: "NEXT_MONTH",
      filterValue: {
        from: now.toISOString(),
        to: add(now, { months: 1 }).toISOString(),
      },
    },
    NEXT_3_MONTH: {
      label: "Expiring Within 3 Months",
      value: "NEXT_3_MONTH",
      filterValue: {
        from: now.toISOString(),
        to: add(now, { months: 3 }).toISOString(),
      },
    },
    NEXT_6_MONTH: {
      label: "Expiring Within 6 Months",
      value: "NEXT_6_MONTH",
      filterValue: {
        from: now.toISOString(),
        to: add(now, { months: 6 }).toISOString(),
      },
    },
    THIS_YEAR: {
      label: "Expiring This Year",
      value: "THIS_YEAR",
      filterValue: {
        from: now.toISOString(),
        to: add(startOfYear(now), { years: 1 }).toISOString(),
      },
    },
    EXPIRED: {
      label: "Already Expired",
      value: "EXPIRED",
      filterValue: {
        from: null,
        to: now.toISOString(),
      },
    },
  };
}

function isValidTimeFilterParam(
  param: any,
): param is keyof ReturnType<typeof getAvailableExpiresFilters> {
  return Object.keys(getAvailableExpiresFilters()).includes(param);
}

function formatStatusLabel(label: string) {
  return upperFirst(label.split("_").join(" ").toLowerCase());
}
